/**
 * "An interviewer declined, or never answered" — on the Overdue tab.
 *
 * The board could always say an interview was booked and never that a panelist
 * had declined it or left the invite untouched. Both end the same way, with
 * nobody in the room, and both are sitting in Google Calendar the whole time
 * (Theresa, Sep 2026).
 *
 * Loaded when this tab renders rather than with the board: one calendar read per
 * upcoming interview is too much to spend on every board build, and this is the
 * only place it is read.
 */
import { useEffect, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { fetchInviteResponses, type InviteResponseRow } from '../../services/taAskApi';
import { COLORS, firstNameFromEmail } from './format';

const TZ = 'Europe/Berlin';
const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        timeZone: TZ,
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : 'time unknown';

const names = (people: { email: string; name: string | null }[]) =>
  people.map((p) => p.name || firstNameFromEmail(p.email)).join(', ');

export function InviteResponsesSection({ boardEmail }: { boardEmail: string }) {
  const [state, setState] = useState<
    | { loading: true }
    | {
        loading: false;
        rows: InviteResponseRow[];
        error?: string;
        capped: boolean;
        /** How many interviews were actually looked at — 0 means there were none. */
        checked: number;
      }
  >({ loading: true });

  /* eslint-disable react-hooks/set-state-in-effect -- every setState runs in an
     async continuation, not in the effect body. */
  useEffect(() => {
    let live = true;
    setState({ loading: true });
    fetchInviteResponses(boardEmail)
      .then(
        (r) =>
          live &&
          setState({ loading: false, rows: r.rows, error: r.error, capped: r.capped, checked: r.checked })
      )
      .catch(
        () =>
          live &&
          setState({
            loading: false,
            rows: [],
            capped: false,
            checked: 0,
            error: 'Could not read the invite responses.',
          })
      );
    return () => {
      live = false;
    };
  }, [boardEmail]);
  /* eslint-enable react-hooks/set-state-in-effect */

  if (state.loading) {
    return <p className="text-xs text-gray-400">Checking who has accepted their interview invites…</p>;
  }
  /**
   * An error is stated, never swallowed. "No warnings" and "we could not look"
   * are the same picture on screen and opposite facts, and the second one is
   * the one that gets somebody stood up.
   */
  if (state.error) {
    return (
      <p className="rounded px-2 py-1 text-xs" style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}>
        {state.error} Check the calendar invites by hand for anything in the next few days.
      </p>
    );
  }
  if (state.rows.length === 0) {
    /**
     * "Nothing to check" and "everyone accepted" are opposite facts that look
     * identical as an empty list, and only one of them is reassuring. A board
     * with no upcoming interviews must not be told its interviewers have all
     * accepted.
     */
    return (
      <p className="text-xs text-gray-400">
        {state.checked === 0
          ? 'No upcoming interviews to check in the next three weeks.'
          : `Every interviewer has accepted — ${state.checked} upcoming interview${
              state.checked === 1 ? '' : 's'
            } checked.`}
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {state.rows.map((row, i) => (
        <li
          key={`${row.opportunityId}-${row.start}-${i}`}
          className="rounded-md border bg-white p-2.5"
          style={{ borderColor: row.declined.length > 0 ? COLORS.redText : COLORS.orangeText }}
        >
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-semibold text-gray-900">{row.candidateName || 'A candidate'}</span>
            <span className="text-xs text-gray-500">
              {row.stageName || 'interview'} · {when(row.start)}
            </span>
          </div>
          {row.unreadable ? (
            <p className="mt-1 text-xs" style={{ color: COLORS.orangeText }}>
              The calendar entry could not be read, so who has accepted is unknown — check it by hand.
            </p>
          ) : (
            <div className="mt-1 space-y-0.5 text-xs">
              {/* A decline first, and worded as a fact: someone has said no, and
                  the interview needs a different person or a different time. */}
              {row.declined.length > 0 && (
                <p style={{ color: COLORS.redText }}>
                  <span className="font-semibold">Declined:</span> {names(row.declined)} — this seat
                  needs re-filling or the interview needs moving.
                </p>
              )}
              {row.awaiting.length > 0 && (
                <p style={{ color: COLORS.orangeText }}>
                  <span className="font-semibold">No answer yet:</span> {names(row.awaiting)} — nudge
                  them before the day.
                </p>
              )}
            </div>
          )}
        </li>
      ))}
      {state.capped && (
        <li className="text-xs text-gray-400">
          Only the soonest interviews were checked — re-open this tab after clearing these.
        </li>
      )}
    </ul>
  );
}

/** The section wrapper, so the Overdue tab reads the same as its siblings. */
export function InviteResponsesBlock({ boardEmail }: { boardEmail: string }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-gray-800">Interview invites — declined or unanswered</p>
        <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : 'Show'}
        </Button>
      </div>
      {open && <InviteResponsesSection boardEmail={boardEmail} />}
    </div>
  );
}
